import {Component, Inject, OnInit} from '@angular/core';
import {MAT_LEGACY_DIALOG_DATA, MatLegacyDialogRef} from '@angular/material/legacy-dialog';
import {ListView} from '@domain/common/list-view/list-view';
import {TypeFilter} from '@domain/common/list-view/type-filter';
import {TypeComparaison} from '@domain/common/list-view/type-comparaison';
import {Result} from '@domain/common/http/result';
import {Page} from '@domain/common/http/list-result';
import {BalisesListItemComponent} from './balises-list-item/balises-list-item.component';
import {Balise} from '@domain/admin/bibliotheque/notification/balise';
import {TypePortee} from '@domain/workflow/workflow';
import {TranslateService} from '@ngx-translate/core';

/**
 * Liste des balises
 */
@Component({
    selector: 'balises-popup',
    templateUrl: './balises-popup.component.html'
})
export class BalisesPopupComponent implements OnInit {
    /** Liste des balises */
    listeBalises: ListView<Balise, BalisesListItemComponent>;

    /**
     * Constructeur
     *
     * @param DialogRef Référence de la boîte de dialogue
     * @param data Données d'entrée de la popin (contient l'éditeur TinyMCE utilisé pour insérer les balises)
     * @param translateService Service de traduction
     */
    constructor(
        private dialogRef: MatLegacyDialogRef<BalisesPopupComponent>,
        @Inject(MAT_LEGACY_DIALOG_DATA) public data: {editor: any},
        private translateService: TranslateService
    ) {}

    /**
     * Initialisation
     */
    ngOnInit(): void {
        this.initialiserListView();
    }

    /**
     * Initialisation de la liste des balises
     */
    private initialiserListView(): void {
        this.listeBalises = new ListView<Balise, BalisesListItemComponent>({
            title: this.translateService.instant('admin.bibliotheque.notifications.message.detail.balise.balises'),
            uri: '/controller/Notification/balisesList',
            defaultOrder: 'idPortee,nom',
            component: BalisesListItemComponent,
            isInfiniteScroll: true,
            isFilter: true,
            isSearchWhenNoFilter: true,
            listeFilters: [
                {
                    clef: 'description',
                    title: this.translateService.instant('admin.bibliotheque.notifications.message.detail.balise.description'),
                    typeComparaison: TypeComparaison[TypeComparaison.LIKE],
                    type: TypeFilter[TypeFilter.STRING],
                    isDefault: true
                },
                {
                    clef: 'nom',
                    title: this.translateService.instant('admin.bibliotheque.notifications.message.detail.balise.balise'),
                    typeComparaison: TypeComparaison[TypeComparaison.LIKE],
                    type: TypeFilter[TypeFilter.STRING],
                    isDefault: true
                },
                {
                    clef: 'idPortee',
                    title: this.translateService.instant('admin.bibliotheque.notifications.message.detail.balise.module'),
                    typeComparaison: TypeComparaison[TypeComparaison.EQUAL],
                    type: TypeFilter[TypeFilter.STRING],
                    listeValues: [
                        {code: '', libelle: this.translateService.instant('admin.bibliotheque.notifications.message.detail.balise.communes')},
                        {code: TypePortee.NF, libelle: this.getLibellePortee(TypePortee.NF)},
                        {code: TypePortee.OD, libelle: this.getLibellePortee(TypePortee.OD)},
                        {code: TypePortee.FC, libelle: this.getLibellePortee(TypePortee.FC)},
                        {code: TypePortee.AV, libelle: this.getLibellePortee(TypePortee.AV)},
                        {code: TypePortee.OT, libelle: this.getLibellePortee(TypePortee.OT)},
                        {code: TypePortee.VP, libelle: this.getLibellePortee(TypePortee.VP)},
                        {code: TypePortee.OP, libelle: this.getLibellePortee(TypePortee.OP)},
                        {code: TypePortee.DV, libelle: this.getLibellePortee(TypePortee.DV)}
                    ]
                }
            ],
            mapResult: (result: Result | Page<Balise>) => {
                const r = result as Result;
                return (r?.data?.pageBalises as Page<Balise>) || null;
            }
        });
    }

    /**
     * Insertion des balises sélectionnées
     */
    insererSelection(): void {
        // Récupération de l'éditeur
        const editor = this.data?.editor;

        // Si l'éditeur n'est pas défini, on ferme la popup
        if (!editor) {
            this.dialogRef.close();
            return;
        }

        //Balises sélectionnées
        const selection = this.listeBalises?.listeObjetsSectionnes || [];

        //Contenu à insérer
        const contenu = selection
            .map(b => b?.nom)
            .filter(nom => !!nom)
            .map(nom => `&lt;${nom}&gt;`)
            .join(' ');

        // Si le contenu n'est pas vide, on l'insère
        if (contenu) {
            editor.insertContent(contenu);
        }

        //Fermeture de la popup
        this.dialogRef.close();
    }

    /**
     * Libellé de la portée pour l'affichage
     *
     * @param idPortee Id de la portée
     * @returns Libellé de la portée
     */
    private getLibellePortee(idPortee: string): string {
        //Récupération du libellé de la portée
        const clef = `portee.${idPortee}`;
        const libelle = this.translateService.instant(clef);

        //Si le libellé n'est pas défini, on retourne l'id de la portée
        return libelle !== clef ? libelle : idPortee;
    }
}
